import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import AdminPlanGrant from '@features/billing/components/AdminPlanGrant';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { useBreadcrumbLabel } from '@shared/contexts/BreadcrumbLabelContext';
import {
  Building2,
  CreditCard,
  Database,
  HardDrive,
  Users,
  ArrowLeft,
  UserCog,
  Ban,
  CheckCircle,
} from 'lucide-react';

interface CustomerDetailsData {
  id: string;
  name: string;
  billing_email: string | null;
  slug: string;
  tax_id: string | null;
  data_region: string | null;
  created_at: string;
  updated_at: string;
  plan_name: string | null;
  plan_amount: number;
  status: string | null;
  current_period_start?: string;
  current_period_end?: string;
  agent_count: number;
  backup_config_count: number;
  total_storage_gb: number;
  total_backup_count: number;
}

export default function CustomerDetails() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.resolvedLanguage || 'en';
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const [details, setDetails] = useState<CustomerDetailsData | null>(null);
  useBreadcrumbLabel(details && details.id === id ? details.name : null);
  const [users, setUsers] = useState<any[]>([]);
  const [usersError, setUsersError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const token = getToken();
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!actionLoading && (showSuspendModal || showImpersonateModal)) {
      const timer = window.setTimeout(
        () =>
          modalRef.current
            ?.querySelector<HTMLElement>('input:not(:disabled), button:not(:disabled)')
            ?.focus(),
        0
      );
      return () => window.clearTimeout(timer);
    }
  }, [actionLoading, showSuspendModal, showImpersonateModal]);
  useEffect(() => {
    if (!showSuspendModal && !showImpersonateModal) return;
    const previous = document.activeElement as HTMLElement | null;
    modalRef.current?.querySelector<HTMLElement>('input, button')?.focus();
    return () => previous?.focus();
  }, [showSuspendModal, showImpersonateModal]);
  const handleModalKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !actionLoading) {
      setShowSuspendModal(false);
      setShowImpersonateModal(false);
    }
    if (event.key !== 'Tab') return;
    const nodes = modalRef.current?.querySelectorAll<HTMLElement>(
      'input:not(:disabled), button:not(:disabled)'
    );
    if (!nodes?.length) {
      event.preventDefault();
      return;
    }
    const first = nodes[0],
      last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (id) {
      Promise.all([fetchCustomerDetails(), fetchOrganizationUsers()]);
    }
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/v1/admin/customer-details?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setDetails(await res.json());
        setError(null);
      } else if (res.status === 403) {
        setError(t('customerDetails.errors.accessDenied'));
      } else if (res.status === 404) {
        setError(t('customerDetails.errors.notFound'));
      } else {
        setError(t('customerDetails.errors.loadFailed'));
      }
    } catch {
      setError(t('customerDetails.errors.connectionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationUsers = async () => {
    try {
      const res = await fetch(`/api/v1/admin/customer-users?organization_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('users load failed');
      setUsers((await res.json()).users || []);
      setUsersError(false);
    } catch (e) {
      console.error(e);
      setUsersError(true);
    }
  };

  const handleImpersonate = async () => {
    if (!selectedUser || actionLoading) return;
    setActionLoading(true);
    setActionMessage(null);

    try {
      const currentToken = getToken();
      if (currentToken) sessionStorage.setItem('original_admin_token', currentToken);

      const res = await fetch('/api/v1/admin/customer-impersonate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUser.id }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('auth_token', data.token);
        sessionStorage.setItem('is_impersonating', 'true');
        sessionStorage.setItem('impersonated_user_id', data.user.id);
        sessionStorage.setItem('impersonated_user_email', data.user.email);
        sessionStorage.setItem('impersonated_user_name', data.user.full_name);
        sessionStorage.setItem('impersonated_by', data.impersonated_by);
        sessionStorage.setItem('impersonated_user_org_id', data.organization.id);
        sessionStorage.setItem('impersonated_user_org_name', data.organization.name);
        if (data.admin_info) {
          sessionStorage.setItem('impersonated_by_admin_email', data.admin_info.email);
          sessionStorage.setItem('impersonated_by_admin_name', data.admin_info.full_name);
        }
        sessionStorage.setItem('impersonation_started_at', new Date().toISOString());
        window.location.href = '/dashboard';
      } else {
        throw new Error('Impersonation request failed');
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: t('customerDetails.errors.startImpersonation'),
      });
      sessionStorage.removeItem('original_admin_token');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser || actionLoading) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch('/api/v1/admin/customer-suspend', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUser.id, reason: suspendReason }),
      });
      if (res.ok) {
        setActionMessage({
          type: 'success',
          text: t('customerDetails.userSuspended', { name: selectedUser.full_name }),
        });
        setShowSuspendModal(false);
        fetchOrganizationUsers();
      } else throw new Error((await res.json()).error);
    } catch {
      setActionMessage({ type: 'error', text: t('customerDetails.errors.suspend') });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async (userId: string) => {
    if (actionLoading) return;
    const confirmed = await showConfirm(
      t('customerDetails.unsuspendConfirmTitle'),
      t('customerDetails.unsuspendConfirmMessage'),
      'warning'
    );
    if (!confirmed) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch('/api/v1/admin/customer-unsuspend', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        setActionMessage({ type: 'success', text: t('customerDetails.userUnsuspended') });
        await fetchOrganizationUsers();
      } else throw new Error((await res.json()).error);
    } catch {
      setActionMessage({ type: 'error', text: t('customerDetails.errors.unsuspend') });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <>
        <div className="py-20 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </>
    );

  if (error || !details)
    return (
      <>
        <div role="alert" className="py-20 text-center space-y-4">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
            {t('customerDetails.errorTitle')}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark mb-6">
            {error || t('customerDetails.errors.loadFailed')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => navigate('/admin/customers')}>{t('back_to_customers')}</Button>
            <Button onClick={() => void fetchCustomerDetails()}>{t('common:actions.retry')}</Button>
          </div>
        </div>
      </>
    );

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')}>
            <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              {details.name}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
              {t('customerDetails.overview')}
            </p>
          </div>
        </div>

        {actionMessage && (
          <div
            role={actionMessage.type === 'error' ? 'alert' : 'status'}
            className={`p-4 rounded-xl border font-bold ${
              actionMessage.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {actionMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Organization Info */}
            <Card className="p-6">
              <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2 mb-6">
                <Building2 className="text-brand-600 dark:text-brand-400" />{' '}
                {t('customerDetails.organization')}
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                <DetailItem label={t('customerDetails.orgId')} value={details.id} mono />
                <DetailItem label={t('customerDetails.name')} value={details.name} />
                <DetailItem
                  label={t('customerDetails.billingEmail')}
                  value={details.billing_email || t('customerDetails.notProvided')}
                />
                <DetailItem label={t('customerDetails.slug')} value={details.slug} />
                <DetailItem
                  label={t('customerDetails.taxId')}
                  value={details.tax_id || t('customerDetails.notProvided')}
                />
                <DetailItem
                  label={t('customerDetails.dataRegion')}
                  value={
                    details.data_region?.toUpperCase() === 'GLOBAL'
                      ? t('customerDetails.regions.global')
                      : details.data_region || t('customerDetails.defaultRegion')
                  }
                />
                <DetailItem
                  label={t('customerDetails.created')}
                  value={new Date(details.created_at).toLocaleDateString(locale)}
                />
              </dl>
            </Card>

            {/* Subscription */}
            <AdminPlanGrant organizationId={id} onGranted={fetchCustomerDetails} />
            <Card className="p-6">
              <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2 mb-6">
                <CreditCard className="text-state-ok-light dark:text-state-ok-dark" />{' '}
                {t('customerDetails.subscription')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DetailItem
                  label={t('customerDetails.plan')}
                  value={details.plan_name || t('customerDetails.noPlan')}
                />
                <DetailItem
                  label={t('customerDetails.amount')}
                  value={
                    Number.isFinite(details.plan_amount)
                      ? `${new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(details.plan_amount / 100)}${t('customerDetails.perMonth')}`
                      : t('customerDetails.notAvailable')
                  }
                />
                <div>
                  <dt className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                    {t('customerDetails.status')}
                  </dt>
                  <dd>
                    <Badge variant={toneToBadgeVariant[getStatusTone(details.status)]}>
                      {t(`customerDetails.statuses.${(details.status || 'none').toLowerCase()}`, {
                        defaultValue: details.status || t('customerDetails.notAvailable'),
                      })}
                    </Badge>
                  </dd>
                </div>
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Usage Stats */}
            <Card className="p-6">
              <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6">
                {t('customerDetails.usage')}
              </h2>
              <div className="space-y-4">
                <StatRow
                  label={t('customerDetails.agents')}
                  value={details.agent_count}
                  icon={<Users size={16} />}
                  color="text-state-info-light dark:text-state-info-dark"
                  bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
                />
                <StatRow
                  label={t('customerDetails.backupConfigs')}
                  value={details.backup_config_count}
                  icon={<Database size={16} />}
                  color="text-state-ok-light dark:text-state-ok-dark"
                  bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
                />
                <StatRow
                  label={t('customerDetails.totalBackups')}
                  value={details.total_backup_count}
                  icon={<Database size={16} />}
                  color="text-accent-text-light dark:text-accent-text-dark"
                  bg="bg-accent-soft-light dark:bg-accent-soft-dark"
                />
                <StatRow
                  label={t('customerDetails.storage')}
                  value={`${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(details.total_storage_gb)} GB`}
                  icon={<HardDrive size={16} />}
                  color="text-state-warn-light dark:text-state-warn-dark"
                  bg="bg-state-warn-bg-light dark:bg-state-warn-bg-dark"
                />
              </div>
            </Card>

            {/* Users */}
            <Card className="p-6">
              <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4">
                {t('customerDetails.users')}
              </h2>
              <div className="space-y-3">
                {usersError && (
                  <div
                    role="alert"
                    className="space-y-3 p-4 text-state-danger-light dark:text-state-danger-dark"
                  >
                    <p>{t('customerDetails.errors.usersLoad')}</p>
                    <Button onClick={() => void fetchOrganizationUsers()}>
                      {t('common:actions.retry')}
                    </Button>
                  </div>
                )}
                {!usersError &&
                  users.map((u) => (
                    <div
                      key={u.id}
                      className="p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white text-sm">
                            {u.full_name}
                          </p>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {u.email}
                          </p>
                        </div>
                        <Badge
                          variant={u.is_suspended ? 'error' : 'neutral'}
                          className="text-[10px]"
                        >
                          {t(`customerDetails.roles.${String(u.role).toLowerCase()}`, {
                            defaultValue: u.role,
                          })}
                        </Badge>
                      </div>

                      <div className="flex gap-2 mt-2">
                        {!u.is_suspended ? (
                          <>
                            <Button
                              disabled={actionLoading}
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark"
                              onClick={() => {
                                setSelectedUser(u);
                                setActionMessage(null);
                                setShowImpersonateModal(true);
                              }}
                            >
                              <UserCog size={12} className="mr-1" />{' '}
                              {t('customerDetails.impersonate')}
                            </Button>
                            <Button
                              disabled={actionLoading}
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark text-state-danger-light dark:text-state-danger-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark"
                              onClick={() => {
                                setSelectedUser(u);
                                setActionMessage(null);
                                setShowSuspendModal(true);
                              }}
                            >
                              <Ban size={12} className="mr-1" /> {t('customerDetails.suspend')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            disabled={actionLoading}
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark text-state-ok-light dark:text-state-ok-dark hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark"
                            onClick={() => handleUnsuspend(u.id)}
                          >
                            <CheckCircle size={12} className="mr-1" />{' '}
                            {t('customerDetails.unsuspend')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Impersonate Modal */}
        {showImpersonateModal && selectedUser && (
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('customerDetails.impersonateUser')}
            onKeyDown={handleModalKey}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          >
            <Card className="w-full max-w-md shadow-elevated p-6">
              <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">
                {t('customerDetails.impersonateUser')}
              </h3>
              <p className="text-text-muted-light dark:text-text-muted-dark mb-6 text-sm">
                {t('customerDetails.impersonateWarning')}{' '}
                <strong className="text-slate-900 dark:text-white">{selectedUser.full_name}</strong>
                {t('customerDetails.actionsLogged')}
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  disabled={actionLoading}
                  onClick={() => setShowImpersonateModal(false)}
                >
                  {t('customerDetails.cancel')}
                </Button>
                <Button onClick={handleImpersonate} isLoading={actionLoading}>
                  {t('customerDetails.impersonate')}
                </Button>
              </div>
              {actionMessage?.type === 'error' && (
                <p
                  role="alert"
                  className="mt-4 text-sm text-state-danger-light dark:text-state-danger-dark"
                >
                  {actionMessage.text}
                </p>
              )}
            </Card>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && selectedUser && (
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('customerDetails.suspendUser')}
            onKeyDown={handleModalKey}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          >
            <Card className="w-full max-w-md shadow-elevated p-6">
              <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">
                {t('customerDetails.suspendUser')}
              </h3>
              <p className="text-text-muted-light dark:text-text-muted-dark mb-4 text-sm">
                {t('customerDetails.suspendConfirmMessage', { name: selectedUser.full_name })}
              </p>
              <Input
                disabled={actionLoading}
                label={t('customerDetails.reason')}
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder={t('violation_of_tos')}
                className="mb-6"
              />
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  disabled={actionLoading}
                  onClick={() => setShowSuspendModal(false)}
                >
                  {t('customerDetails.cancel')}
                </Button>
                <Button
                  onClick={handleSuspend}
                  isLoading={actionLoading}
                  className="bg-state-danger-light hover:opacity-90 dark:bg-state-danger-dark"
                >
                  {t('customerDetails.suspendAccount')}
                </Button>
              </div>
              {actionMessage?.type === 'error' && (
                <p
                  role="alert"
                  className="mt-4 text-sm text-state-danger-light dark:text-state-danger-dark"
                >
                  {actionMessage.text}
                </p>
              )}
            </Card>
          </div>
        )}
      </div>
    </>
  );
}

function DetailItem({ label, value, mono }: any) {
  return (
    <div>
      <dt className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
        {label}
      </dt>
      <dd
        className={`text-sm font-medium text-slate-900 dark:text-white ${mono ? 'font-mono bg-surface-2-light dark:bg-surface-2-dark px-2 py-1 rounded inline-block' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

function StatRow({ label, value, icon, color, bg }: any) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-xl ${bg}`}>
      <div className="flex items-center gap-3">
        <div
          className={`p-1.5 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50 ${color}`}
        >
          {icon}
        </div>
        <span className={`text-sm font-bold ${color.replace('text-', 'text-slate-')}`}>
          {label}
        </span>
      </div>
      <span className={`font-black ${color}`}>{value}</span>
    </div>
  );
}
